import { Badge, Flex, Text, Select, Grid, TextArea } from "@radix-ui/themes";
import {
  TrackerColumnSort,
  Tracker,
  TrackerItem,
} from "@/v2/entities/tracker/Tracker.model";
import { TrackerStageIcon } from "@/v2/assets/mor-shapes/TrackerStageIcon";
import { useTrackerApi } from "@/v2/entities/tracker/Tracker.api";
import { useMutation } from "react-query";
import { TrackerItemAudit } from "./TrackerItemAudit";

import styles from "./TrackerItemView.module.scss";

interface TrackerItemViewProps {
  trackerItem: TrackerItem;
  tracker: Tracker;
  refetch: () => void;
}

function TrackerItemView({
  trackerItem,
  tracker,
  refetch,
}: TrackerItemViewProps) {
  const trackerApi = useTrackerApi();

  const { mutate: updateTrackerItemColumn } = useMutation({
    mutationKey: ["updateTrackerItemColumn", tracker.id, trackerItem.id],
    mutationFn: (newColumnId: string) =>
      trackerApi.updateTrackerItemColumn(
        tracker.id,
        trackerItem.id,
        newColumnId
      ),
    onSuccess: (data, variables) => {
      refetch();
    },
  });

  const { mutate: updateTrackerItemComment } = useMutation({
    mutationKey: ["updateTrackerItemComment", tracker.id, trackerItem.id],
    mutationFn: (newComment: string) =>
      trackerApi.updateTrackerItemComment(
        tracker.id,
        trackerItem.id,
        newComment
      ),
    onSuccess: () => {
      refetch();
    },
  });

  return (
    <Flex className={styles.TrackerItemView} direction={"column"} gap={"1"}>
      {/* Header with AI Improvement badge */}
      <Flex align={"center"} gap={"2"} p={"2"}>
        <Badge size={"3"} color={"gray"}>
          <TrackerStageIcon stage={trackerItem.stage} />
          {tracker.name}
        </Badge>
      </Flex>

      <Grid columns={"2"} gap={"5"} width={"100%"} p={"4"}>
        <Text size={"2"} color={"gray"}>
          Stage
        </Text>

        <Select.Root
          size={"1"}
          defaultValue={trackerItem.columnId}
          onValueChange={(value) => {
            updateTrackerItemColumn(value);
          }}
        >
          <Select.Trigger />
          <Select.Content>
            {tracker.columns.sort(TrackerColumnSort).map((option) => (
              <Select.Item key={option.id} value={option.id}>
                <Flex as="span" align="center" gap="2">
                  <TrackerStageIcon
                    stage={option.stage}
                    width={12}
                    height={18}
                  />
                  {option.name}
                </Flex>
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Root>

        <Text size={"2"} color={"gray"}>
          Comment
        </Text>
        <TextArea
          defaultValue={trackerItem.comment}
          size={"1"}
          placeholder="Add comment..."
          onBlur={(e) => {
            updateTrackerItemComment(e.target.value);
          }}
        />
      </Grid>

      <TrackerItemAudit
        trackerItemEvents={trackerItem.events ?? []}
        trackerColumns={tracker.columns}
      />
    </Flex>
  );
}

export default TrackerItemView;
